/** Minimum spacing between /version.json fetches, shared across all triggers. */
export const VERSION_CHECK_MIN_INTERVAL_MS = 60_000;

/** Sentinel build id for local dev servers; disables checking entirely. */
export const DEV_BUILD_ID = 'dev';

export type VersionCheckListener = (isStale: boolean) => void;

/**
 * Detects when the running bundle is older than the currently deployed one by
 * comparing the compiled-in build id against the deploy's /version.json
 * (emitted by the build, served with Cache-Control: no-store).
 *
 * Staleness latches: once detected it never resets without a reload, and no
 * further fetches are made. All failure modes (network error, non-200,
 * malformed body) are silent — a missed check just waits for the next trigger.
 */
export class VersionCheckInteractor {
  private isStaleInternal = false;
  private lastCheckAt: number | null = null;
  private inFlight = false;
  private listeners = new Set<VersionCheckListener>();

  constructor(
    private readonly buildId: string,
    private readonly fetchFn: typeof fetch = (...args) => fetch(...args),
    private readonly now: () => number = () => Date.now(),
  ) {}

  get isStale(): boolean {
    return this.isStaleInternal;
  }

  /** Checking is pointless without a deploy stamp (local dev server). */
  get isEnabled(): boolean {
    return this.buildId !== DEV_BUILD_ID;
  }

  subscribe(listener: VersionCheckListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Fetch /version.json and latch stale state if the deployed build id
   * differs. Callers fire this from every trigger (visibility, focus, tree
   * load); throttling and the stale latch make it safe to call freely.
   */
  async check(): Promise<void> {
    if (!this.isEnabled || this.isStaleInternal || this.inFlight) {
      return;
    }
    const now = this.now();
    if (this.lastCheckAt !== null && now - this.lastCheckAt < VERSION_CHECK_MIN_INTERVAL_MS) {
      return;
    }
    this.lastCheckAt = now;
    this.inFlight = true;
    try {
      const response = await this.fetchFn('/version.json', { cache: 'no-store' });
      if (!response.ok) {
        return;
      }
      const body: unknown = await response.json();
      const deployedBuildId =
        typeof body === 'object' && body !== null && 'buildId' in body
          ? (body as { buildId: unknown }).buildId
          : undefined;
      if (typeof deployedBuildId !== 'string' || deployedBuildId === '') {
        return;
      }
      if (deployedBuildId !== this.buildId) {
        this.isStaleInternal = true;
        this.listeners.forEach(listener => listener(true));
      }
    } catch {
      // Silent by design; a later trigger retries.
    } finally {
      this.inFlight = false;
    }
  }
}

/**
 * App-wide instance. `typeof` guard keeps imports safe under tooling that
 * doesn't apply the vite `define` (e.g. plain tsc); there it behaves as dev.
 */
export const versionCheckInteractor = new VersionCheckInteractor(
  typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : DEV_BUILD_ID,
);
